import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import OemBrandingBox from "@/components/OemBrandingBox";
import { BadgeCheck, FileText, ListChecks, Paperclip, PenLine, StickyNote } from "lucide-react";

const Chip = ({ checked, onToggle, title, testid }) => (
  <label title={title} data-testid={testid}
    className={`inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${checked ? "border-[#1B4383] bg-[#E6ECF5] text-[#1B4383]" : "border-border bg-white text-slate-600 hover:border-[#1B4383]/40"}`}>
    <Checkbox checked={checked} onCheckedChange={onToggle} className="h-3.5 w-3.5" />
    <span className="truncate">{title}</span>
  </label>
);

const Group = ({ label, children }) => (
  <div className="space-y-1.5">
    <div className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

const Row = ({ icon: Icon, title, summary, done, children, testid }) => (
  <div className="space-y-2 border-t border-border px-4 py-3 first:border-t-0" data-testid={testid}>
    <div className="flex items-start gap-2">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${done ? "text-[#527D63]" : "text-slate-400"}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#1B2A4A]">{title}</div>
        <div className="truncate text-[11px] text-muted-foreground" data-testid={`${testid}-summary`}>{summary}</div>
      </div>
    </div>
    {children}
  </div>
);

const Attachments = ({ companyDocs, oemLetters, datasheetGroups, attachSel, supAttachSel, toggleDoc, toggleSup }) => (
  <div className="space-y-2.5" data-testid="cq-attachments">
    {datasheetGroups.map((g) => (
      <Group key={g.supplier_id} label={g.supplier_name}>
        {g.files.map((d) => <Chip key={d.id} checked={!!supAttachSel[d.id]} onToggle={() => toggleSup(d.id)} title={d.title} testid={`cq-supds-${d.id}`} />)}
      </Group>
    ))}
    {oemLetters.filter((g) => g.letters.length).map((g) => (
      <Group key={g.supplier_id} label={`OEM Letters · ${g.supplier_name}`}>
        {g.letters.map((d) => <Chip key={d.id} checked={!!supAttachSel[d.id]} onToggle={() => toggleSup(d.id)} title={d.title} testid={`cq-suplet-${d.id}`} />)}
      </Group>
    ))}
    {companyDocs.length > 0 && (
      <Group label="Company Documents">
        {companyDocs.map((d) => <Chip key={d.id} checked={!!attachSel[d.id]} onToggle={() => toggleDoc(d.id)} title={d.title} testid={`cq-attach-${d.id}`} />)}
      </Group>
    )}
  </div>
);

const EditDialog = ({ open, onOpenChange, title, wide, children, testid }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={wide ? "max-w-3xl" : "max-w-lg"} data-testid={testid}>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="max-h-[70vh] overflow-y-auto pr-1">{children}</div>
      <DialogFooter><Button onClick={() => onOpenChange(false)} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid={`${testid}-done`}>Done</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export const QuoteChecklist = ({ cq, setHeader, awardedSupplierIds, defaultNotes, totalSel, ...att }) => {
  const [oemOpen, setOemOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const oem = cq.oem || {};
  const specCount = (cq.lines || []).filter((l) => l.tech_spec).length;
  const missingLetters = att.oemLetters.filter((g) => !g.letters.length).map((g) => g.supplier_name);
  return (
    <aside className="h-fit overflow-hidden rounded-xl border border-border bg-white shadow-sm xl:sticky xl:top-20" data-testid="cq-checklist">
      <div className="flex items-center gap-2 bg-[#1B4383] px-4 py-2.5 text-sm font-bold text-white"><ListChecks className="h-4 w-4" />Quote Checklist</div>
      <Row icon={Paperclip} title="Attachments" done={totalSel > 0} testid="cq-check-attachments"
        summary={totalSel ? `${totalSel} Selected For The Bid Pack & Email` : "Nothing Selected Yet"}>
        <Attachments {...att} />
        {missingLetters.length > 0 && <p className="text-[10px] text-amber-700">No authorisation letter on file for {missingLetters.join(", ")}.</p>}
      </Row>
      <Row icon={BadgeCheck} title="OEM Branding & Terms" done={!!oem.enabled} testid="cq-check-oem"
        summary={oem.enabled ? `${oem.name || "OEM"} \u00b7 ${(oem.terms || []).length} OEM Clause${(oem.terms || []).length === 1 ? "" : "s"}` : "Off \u00b7 Standard Somcore Quote"}>
        <Button variant="outline" size="sm" className="h-7 w-full" onClick={() => setOemOpen(true)} data-testid="cq-check-oem-edit"><PenLine className="mr-1.5 h-3.5 w-3.5" />{oem.enabled ? "Edit OEM Branding & Terms" : "Set Up OEM Branding"}</Button>
      </Row>
      <Row icon={FileText} title="Technical Specification" done={cq.show_specs !== false && specCount > 0} testid="cq-check-specs"
        summary={specCount ? `${specCount} Line${specCount === 1 ? "" : "s"} With Photo / Specs` : "No Linked SKU Has Specs Yet"}>
        <label className="flex items-center gap-2 text-xs text-slate-700" data-testid="cq-show-specs">
          <Checkbox checked={cq.show_specs !== false} onCheckedChange={(v) => setHeader({ show_specs: !!v })} data-testid="cq-show-specs-toggle" />Print On The Quote
        </label>
      </Row>
      <Row icon={StickyNote} title="Special Notes & Conditions" done={!!cq.notes} testid="cq-check-notes"
        summary={cq.notes ? cq.notes : "Standard Notes Will Print"}>
        <Button variant="outline" size="sm" className="h-7 w-full" onClick={() => setNotesOpen(true)} data-testid="cq-check-notes-edit"><PenLine className="mr-1.5 h-3.5 w-3.5" />Edit Notes</Button>
      </Row>
      <EditDialog open={oemOpen} onOpenChange={setOemOpen} title="OEM Branding & Terms" wide testid="cq-oem-dialog">
        <OemBrandingBox oem={cq.oem} onChange={(o) => setHeader({ oem: o })} awardedSupplierIds={awardedSupplierIds} />
      </EditDialog>
      <EditDialog open={notesOpen} onOpenChange={setNotesOpen} title="Special Notes & Conditions" testid="cq-notes-dialog">
        <Textarea rows={6} placeholder={defaultNotes} value={cq.notes || ""} onChange={(e) => setHeader({ notes: e.target.value })} data-testid="cq-notes" />
        <p className="mt-1.5 text-[11px] text-muted-foreground">Leave blank to print the standard notes.</p>
      </EditDialog>
    </aside>
  );
};

export default QuoteChecklist;
